import { apiSlice } from "../baseQuery";
import { EmpresaEndpointTags } from "../utils";
import { Pagination, RegisterCreated } from "../../types/utils";
import {
    CommentQueryOptions,
    ConfirmedDeal,
    CreateContactoRequest,
    CreateEmpresa,
    DeleteVendedorFromDealRequest,
    EmpresaCurrentDeal,
    EmpresaFilters,
    EmpresaInfo,
    EmpresaInfoRequest,
    EmpresaResponse,
    EmpresasResponse,
    StageChangeComments,
    UpdateDealRequest,
    UpdateGeneralInfoRequest,
    UploadEmpresaFileResponse
} from "../../types/empresa";

const BASE_URL = "/api/empresas";

type QueryArgsCommentFromStageChange = { empresaId: number } & CommentQueryOptions;

export const empresaApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getEmpesas: builder.query<EmpresasResponse, EmpresaFilters & Pagination>({
            query: (filters: EmpresaFilters) => {
                const queryString = new URLSearchParams(filters).toString()
                return BASE_URL + `?${queryString}`;
            },
            keepUnusedDataFor: 5,
            providesTags: [EmpresaEndpointTags.GET_EMPRESAS],
        }),
        createEmpresa: builder.mutation<RegisterCreated, CreateEmpresa>({
            query: (request: CreateEmpresa) => ({
                url: BASE_URL,
                method: "POST",
                body: request
            }),
        }),
        getEmpresaById: builder.query<EmpresaResponse, number>({
            query: (empresaId: number) => BASE_URL + `/${empresaId}`,
            providesTags: [EmpresaEndpointTags.GET_EMPRESA_BY_ID]
        }),
        getEmpresaInfoById: builder.query<EmpresaInfo, EmpresaInfoRequest>({
            query: (request: EmpresaInfoRequest) => {
                const query = {
                    section: request.section ?? "",
                };
                const queryString = new URLSearchParams(query).toString()
                return BASE_URL + `/info/${request.empresaId}?${queryString}`
            },
            keepUnusedDataFor: 0,
            providesTags: ["getEmpresaInfoById"]
        }),
        getCurrentDeal: builder.query<EmpresaCurrentDeal, number>({
            query: (empresaId: number) => BASE_URL + `/${empresaId}/current-deal`,
            keepUnusedDataFor: 0,
            providesTags: ["getCurrentDeal"]
        }),
        getCommentsFromStageChanges: builder.query<StageChangeComments[], QueryArgsCommentFromStageChange>({
            query: (queryArgs: QueryArgsCommentFromStageChange) => {
                const { empresaId, ...queryOptions } = queryArgs;
                const queryString = new URLSearchParams(queryOptions).toString();

                return BASE_URL + `/${empresaId}/comments?${queryString}`;
            } ,
        }),
        getEmpresasWithConfirmedDeals: builder.query<ConfirmedDeal[], string | null>({
            query: (date: string | null) => {
                const queryString = new URLSearchParams({ date: date ?? "" })
                return BASE_URL + "/confirmed-deals" + `?${queryString}`;
            },
            providesTags: [ "ConfirmedDeal" ]
        }),
        getTotalOfConfirmedDeals: builder.query<ConfirmedDeal[], string | null>({
            query: (date: string | null) => {
                const queryString = new URLSearchParams({ date: date ?? "" })
                return BASE_URL + "/calendar-deals" + `?${queryString}`;
            },
            providesTags: ["calendarioEmpresas"],
            keepUnusedDataFor: 0,
        }),
        updateGeneralInfo: builder.mutation<void, UpdateGeneralInfoRequest>({
            query: (request: UpdateGeneralInfoRequest) => ({
                url: BASE_URL + `/info/${request.empresaId}`,
                method: "PUT",
                body: request.generalInfo
            }),
            invalidatesTags: ["getEmpresaInfoById", "getEmpresaById"]
        }),
        createContacto: builder.mutation<void, CreateContactoRequest>({
            query: (request: CreateContactoRequest) => ({
                url: BASE_URL + `/${request.empresaId}/contactos`,
                method: "POST",
                body: request.contactoInfo
            }),
            invalidatesTags: ["getEmpresaById", "getEmpresaInfoById"]
        }),
        updateContacto: builder.mutation<void, CreateContactoRequest>({
            query: (request: CreateContactoRequest) => ({
                url: BASE_URL + `/${request.empresaId}/contactos/${request.contactoInfo.id}`,
                method: "PUT",
                body: request.contactoInfo
            }),
            invalidatesTags: ["getEmpresaById", "getEmpresaInfoById"]
        }),
        uploadEmpresaFile: builder.mutation<UploadEmpresaFileResponse, File>({
            query: (file: File) => {
                const bodyFormData = new FormData();
                bodyFormData.append("excelFile", file);
                return {
                    url: BASE_URL + "/upload-bulk",
                    method: "POST",
                    body: bodyFormData,
                }
            },
            invalidatesTags: [EmpresaEndpointTags.GET_EMPRESAS],
        }),
        updateDeal: builder.mutation<void, UpdateDealRequest>({
            query: (request: UpdateDealRequest) => ({
                url: BASE_URL + `/${request.empresaId}/deals/${request.dealId}`,
                method: "PUT",
                body: {
                    id: request.dealId,
                    fechaVisita: request.fechaVisita,
                    vendedores: request.vendedores,
                },
            }),
            invalidatesTags: [ "ConfirmedDeal", "calendarioEmpresas" ],
        }),
        deleteVendedorFromDeal: builder.mutation<void, DeleteVendedorFromDealRequest>({
            query: (request: DeleteVendedorFromDealRequest) => ({
                url: BASE_URL + `/${request.empresaId}/deals/${request.dealId}/vendedores/${request.vendedorId}`,
                method: "DELETE",
            }),
            invalidatesTags: ["ConfirmedDeal"],
        }),
        getImportTemplate: builder.query<Blob, void>({
            query: () => ({
                url: BASE_URL + "/get-import-template",
                responseHandler: async (response) => await response.blob(),
                cache: "no-cache",
            })
        }),
        editComments: builder.mutation<void, { empresaId: number, text: string }>({
            query: (request) => ({
                url: BASE_URL + `/${request.empresaId}/edit-comments`,
                method: "POST",
                body: {
                    text: request.text,
                },
            })
        }),
        changeAssignedProspectador: builder.mutation<void, { empresaId: number, prospectadorId: number }>({
            query: (request) => ({
                url: BASE_URL + `/${request.empresaId}/change-prospectador/${request.prospectadorId}`,
                method: "POST",
            }),
            invalidatesTags: [EmpresaEndpointTags.GET_EMPRESA_BY_ID],
        }),
    })
})

export default empresaApi;

export const { 
    useGetEmpesasQuery,
    useGetEmpresaByIdQuery,
    useGetEmpresaInfoByIdQuery,
    useGetCurrentDealQuery,
    useGetCommentsFromStageChangesQuery,
    useGetEmpresasWithConfirmedDealsQuery,
    useGetTotalOfConfirmedDealsQuery,
    // mutations
    useUpdateGeneralInfoMutation,
    useCreateContactoMutation,
    useUpdateContactoMutation,
    useUploadEmpresaFileMutation,
    useUpdateDealMutation,
    useCreateEmpresaMutation,
    useDeleteVendedorFromDealMutation,
    useEditCommentsMutation,
    useChangeAssignedProspectadorMutation,
    // lazy
    useLazyGetEmpresaInfoByIdQuery,
    useLazyGetImportTemplateQuery,
 } = empresaApi;
